import type { LangMap } from "./index";

/** Revisione docente del feedback AI, vista studente e avvisi scadenze. */
const it: Record<string, unknown> = {
  title: "Feedback AI da rivedere",
  hint: "Il voto e il commento diventano visibili allo studente solo dopo la tua approvazione.",
  pending: "{{n}} da rivedere",
  draft: "Genera bozza AI",
  drafting: "Sto scrivendo...",
  score: "Voto (su {{max}})",
  feedback: "Commento allo studente",
  summary: "Sintesi accessibile",
  approve: "Approva e pubblica",
  approved: "Feedback pubblicato",
  reject: "Rifiuta",
  rejected: "Rifiutata",
  rejectedDone: "Bozza rifiutata",
  student: {
    title: "Il tuo feedback",
    waiting: "Il professore sta rivedendo il tuo risultato: lo vedrai appena approvato.",
    score: "Voto {{score}}/{{max}}",
    simple: "Sintesi",
    full: "Completo",
    listen: "Ascolta",
    stop: "Ferma",
    empty: "Nessun commento.",
    approved: "Feedback approvato dal professore.",
  },
  notify: {
    title: "Compiti e scadenze",
    soon: "In scadenza",
    new: "Nuova assegnazione",
    pushTitle: "Scadenza in arrivo",
    pushBody: "«{{title}}» scade presto: prenditi 10 minuti oggi.",
    prefs: "Compiti e scadenze",
    prefsHint: "Avvisi in app per nuove attività e promemoria prima della consegna.",
    push: "Promemoria push prima della scadenza",
    lead: "Anticipo",
    leadHours: "{{n}} ore",
  },
};

const en: Record<string, unknown> = {
  title: "AI feedback to review",
  hint: "Score and comment reach the student only after you approve them.",
  pending: "{{n}} to review",
  draft: "Generate AI draft",
  drafting: "Writing...",
  score: "Score (out of {{max}})",
  feedback: "Comment for the student",
  summary: "Accessible summary",
  approve: "Approve and publish",
  approved: "Feedback published",
  reject: "Reject",
  rejected: "Rejected",
  rejectedDone: "Draft rejected",
  student: {
    title: "Your feedback",
    waiting: "Your teacher is reviewing your result: you'll see it once approved.",
    score: "Score {{score}}/{{max}}",
    simple: "Summary",
    full: "Full",
    listen: "Listen",
    stop: "Stop",
    empty: "No comment.",
    approved: "Feedback approved by your teacher.",
  },
  notify: {
    title: "Homework and deadlines",
    soon: "Due soon",
    new: "New assignment",
    pushTitle: "Deadline coming up",
    pushBody: "“{{title}}” is due soon: take 10 minutes today.",
    prefs: "Homework and deadlines",
    prefsHint: "In-app alerts for new activities and a reminder before the due date.",
    push: "Push reminder before the deadline",
    lead: "Lead time",
    leadHours: "{{n}} hours",
  },
};

const es: Record<string, unknown> = {
  title: "Feedback de IA por revisar",
  hint: "La nota y el comentario llegan al alumno solo después de tu aprobación.",
  pending: "{{n}} por revisar",
  draft: "Generar borrador IA",
  drafting: "Escribiendo...",
  score: "Nota (sobre {{max}})",
  feedback: "Comentario para el alumno",
  summary: "Resumen accesible",
  approve: "Aprobar y publicar",
  approved: "Feedback publicado",
  reject: "Rechazar",
  rejected: "Rechazado",
  rejectedDone: "Borrador rechazado",
  student: {
    title: "Tu feedback",
    waiting: "Tu profesor está revisando el resultado: lo verás en cuanto lo apruebe.",
    score: "Nota {{score}}/{{max}}",
    simple: "Resumen",
    full: "Completo",
    listen: "Escuchar",
    stop: "Parar",
    empty: "Sin comentarios.",
    approved: "Feedback aprobado por el profesor.",
  },
  notify: {
    title: "Tareas y fechas de entrega",
    soon: "Próxima entrega",
    new: "Nueva tarea",
    pushTitle: "Entrega cercana",
    pushBody: "«{{title}}» vence pronto: dedícale 10 minutos hoy.",
    prefs: "Tareas y fechas de entrega",
    prefsHint: "Avisos en la app para nuevas actividades y recordatorio antes de la entrega.",
    push: "Recordatorio push antes de la entrega",
    lead: "Antelación",
    leadHours: "{{n}} horas",
  },
};

const fr: Record<string, unknown> = {
  title: "Retour IA à relire",
  hint: "La note et le commentaire ne sont visibles par l'élève qu'après ton approbation.",
  pending: "{{n}} à relire",
  draft: "Générer un brouillon IA",
  drafting: "Rédaction...",
  score: "Note (sur {{max}})",
  feedback: "Commentaire pour l'élève",
  summary: "Résumé accessible",
  approve: "Approuver et publier",
  approved: "Retour publié",
  reject: "Refuser",
  rejected: "Refusé",
  rejectedDone: "Brouillon refusé",
  student: {
    title: "Ton retour",
    waiting: "Ton professeur relit ton résultat : tu le verras dès qu'il sera validé.",
    score: "Note {{score}}/{{max}}",
    simple: "Résumé",
    full: "Complet",
    listen: "Écouter",
    stop: "Arrêter",
    empty: "Aucun commentaire.",
    approved: "Retour validé par le professeur.",
  },
  notify: {
    title: "Devoirs et échéances",
    soon: "Bientôt à rendre",
    new: "Nouveau devoir",
    pushTitle: "Échéance proche",
    pushBody: "« {{title}} » arrive à échéance : prends 10 minutes aujourd'hui.",
    prefs: "Devoirs et échéances",
    prefsHint: "Alertes dans l'app pour les nouvelles activités et un rappel avant la date de rendu.",
    push: "Rappel push avant l'échéance",
    lead: "Anticipation",
    leadHours: "{{n}} heures",
  },
};

const de: Record<string, unknown> = {
  title: "KI-Feedback prüfen",
  hint: "Note und Kommentar sieht die Schülerin oder der Schüler erst nach deiner Freigabe.",
  pending: "{{n}} zu prüfen",
  draft: "KI-Entwurf erstellen",
  drafting: "Wird geschrieben...",
  score: "Note (von {{max}})",
  feedback: "Kommentar für die Lernenden",
  summary: "Barrierefreie Zusammenfassung",
  approve: "Freigeben und senden",
  approved: "Feedback veröffentlicht",
  reject: "Ablehnen",
  rejected: "Abgelehnt",
  rejectedDone: "Entwurf abgelehnt",
  student: {
    title: "Dein Feedback",
    waiting: "Deine Lehrkraft prüft dein Ergebnis: du siehst es nach der Freigabe.",
    score: "Note {{score}}/{{max}}",
    simple: "Zusammenfassung",
    full: "Vollständig",
    listen: "Anhören",
    stop: "Stopp",
    empty: "Kein Kommentar.",
    approved: "Feedback von der Lehrkraft freigegeben.",
  },
  notify: {
    title: "Aufgaben und Fristen",
    soon: "Bald fällig",
    new: "Neue Aufgabe",
    pushTitle: "Frist steht an",
    pushBody: "„{{title}}“ ist bald fällig: nimm dir heute 10 Minuten.",
    prefs: "Aufgaben und Fristen",
    prefsHint: "Hinweise in der App für neue Aufgaben und eine Erinnerung vor der Abgabe.",
    push: "Push-Erinnerung vor der Frist",
    lead: "Vorlaufzeit",
    leadHours: "{{n}} Stunden",
  },
};

const pt: Record<string, unknown> = {
  title: "Feedback da IA para rever",
  hint: "A nota e o comentário só chegam ao aluno depois da tua aprovação.",
  pending: "{{n}} para rever",
  draft: "Gerar rascunho IA",
  drafting: "A escrever...",
  score: "Nota (em {{max}})",
  feedback: "Comentário para o aluno",
  summary: "Resumo acessível",
  approve: "Aprovar e publicar",
  approved: "Feedback publicado",
  reject: "Rejeitar",
  rejected: "Rejeitado",
  rejectedDone: "Rascunho rejeitado",
  student: {
    title: "O teu feedback",
    waiting: "O professor está a rever o teu resultado: vais vê-lo assim que for aprovado.",
    score: "Nota {{score}}/{{max}}",
    simple: "Resumo",
    full: "Completo",
    listen: "Ouvir",
    stop: "Parar",
    empty: "Sem comentários.",
    approved: "Feedback aprovado pelo professor.",
  },
  notify: {
    title: "Trabalhos e prazos",
    soon: "A terminar em breve",
    new: "Novo trabalho",
    pushTitle: "Prazo a chegar",
    pushBody: "«{{title}}» termina em breve: dedica-lhe 10 minutos hoje.",
    prefs: "Trabalhos e prazos",
    prefsHint: "Avisos na app para novas atividades e lembrete antes da entrega.",
    push: "Lembrete push antes do prazo",
    lead: "Antecedência",
    leadHours: "{{n}} horas",
  },
};

export const review: LangMap = { it, en, es, fr, de, pt };
